import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

import { useAdminUserEditOptions } from '../../hooks/useApi';
import { cn, pluralize } from '../../lib/utils';
import type { AdminUserEditCompanyOption } from '../../services/api';
import { FieldControlContext, useFieldControl } from '../ui/form-field';
import { Input } from '../ui/input';
import { selectTriggerClassName } from '../ui/select';
import { popoverSurfaceClassName } from '../ui/surface';
import { formatCompanyLabel, formatTeamLabel } from './companyComboboxLabels';

interface CompanySearchKeyboardState {
    activeIndex: number;
    companies: AdminUserEditCompanyOption[];
    query: string;
    choose: (company: AdminUserEditCompanyOption) => void;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function getMovedCompanyIndex(
    key: 'ArrowDown' | 'ArrowUp',
    current: number,
    companyCount: number,
): number {
    return key === 'ArrowDown'
        ? Math.min(current + 1, companyCount - 1)
        : Math.max(current < 0 ? companyCount - 1 : current - 1, 0);
}

function handleCompanyArrowKey(
    event: React.KeyboardEvent<HTMLInputElement>,
    key: 'ArrowDown' | 'ArrowUp',
    state: CompanySearchKeyboardState,
) {
    event.preventDefault();
    state.setActiveIndex((current) => getMovedCompanyIndex(key, current, state.companies.length));
}

function handleCompanyBoundaryKey(
    event: React.KeyboardEvent<HTMLInputElement>,
    key: 'Home' | 'End',
    state: CompanySearchKeyboardState,
) {
    if (state.query !== '') return;
    event.preventDefault();
    state.setActiveIndex(key === 'Home' ? 0 : state.companies.length - 1);
}

function handleCompanyEnterKey(
    event: React.KeyboardEvent<HTMLInputElement>,
    state: CompanySearchKeyboardState,
) {
    event.preventDefault();
    event.stopPropagation();
    const company = state.companies[state.activeIndex];
    if (company) state.choose(company);
}

function handleCompanySearchKey(
    event: React.KeyboardEvent<HTMLInputElement>,
    state: CompanySearchKeyboardState,
) {
    switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
            handleCompanyArrowKey(event, event.key, state);
            return;
        case 'Home':
        case 'End':
            handleCompanyBoundaryKey(event, event.key, state);
            return;
        case 'Enter':
            handleCompanyEnterKey(event, state);
            return;
        case 'Tab':
            state.setOpen(false);
    }
}

export function CompanyCombobox({
    value,
    onSelect,
    ariaLabel,
    selectedCompanyId,
    placeholder = 'Choose a company',
}: {
    value: string;
    onSelect: (company: AdminUserEditCompanyOption) => void;
    ariaLabel: string;
    selectedCompanyId?: number | null;
    placeholder?: string;
}) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const listboxId = React.useId();
    const field = useFieldControl();
    const optionsQuery = useAdminUserEditOptions(query, 25, open);
    const companies = React.useMemo(
        () => optionsQuery.data?.pages.flatMap((page) => page.items) ?? [],
        [optionsQuery.data?.pages],
    );
    const companiesKey = companies.map((company) => company.companyId).join(',');
    const optionId = React.useCallback(
        (index: number) => `${listboxId}-option-${index}`,
        [listboxId],
    );

    React.useEffect(() => {
        setActiveIndex(companies.length > 0 ? 0 : -1);
    }, [companies.length, companiesKey, query]);

    React.useEffect(() => {
        if (activeIndex < 0) return;
        document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex, optionId]);

    React.useEffect(() => {
        if (!open) return;
        const handlePointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [open]);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 16;
        if (nearBottom && optionsQuery.hasNextPage && !optionsQuery.isFetchingNextPage) {
            void optionsQuery.fetchNextPage();
        }
    };

    const choose = (company: AdminUserEditCompanyOption) => {
        onSelect(company);
        setOpen(false);
        triggerRef.current?.focus();
    };

    return (
        <div
            ref={containerRef}
            className="relative"
            onKeyDown={(event) => {
                if (event.key === 'Escape' && open) {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(false);
                    triggerRef.current?.focus();
                }
            }}
        >
            <button
                ref={triggerRef}
                type="button"
                role="combobox"
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-invalid={field?.invalid ? true : undefined}
                aria-describedby={field?.describedBy}
                aria-required={field?.required ? true : undefined}
                className={selectTriggerClassName}
                onClick={() => setOpen((current) => !current)}
                onKeyDown={(event) => {
                    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
            >
                <span
                    className={cn('min-w-0 truncate', !value && 'text-neutral-500')}
                    title={value || undefined}
                >
                    {value || placeholder}
                </span>
                <ChevronDown
                    aria-hidden="true"
                    className={cn(
                        'size-4 shrink-0 text-neutral-500 transition-transform',
                        open && 'rotate-180',
                    )}
                />
            </button>
            {open ? (
                <div className={cn(popoverSurfaceClassName, 'absolute z-50 mt-1 w-full p-2')}>
                    <FieldControlContext.Provider value={null}>
                        <Input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={(event) =>
                                handleCompanySearchKey(event, {
                                    activeIndex,
                                    companies,
                                    query,
                                    choose,
                                    setActiveIndex,
                                    setOpen,
                                })
                            }
                            placeholder="Search company name or ID"
                            aria-label="Search companies"
                            aria-controls={listboxId}
                            aria-autocomplete="list"
                            aria-activedescendant={
                                activeIndex >= 0 ? optionId(activeIndex) : undefined
                            }
                            autoComplete="off"
                            autoFocus
                        />
                        <div
                            className="mt-2 max-h-56 overflow-y-auto"
                            role="listbox"
                            id={listboxId}
                            aria-label={ariaLabel}
                            onScroll={handleScroll}
                        >
                            {companies.map((company, index) => {
                                const selected = company.companyId === selectedCompanyId;
                                const teamSummary =
                                    company.teams.length === 0
                                        ? '0 teams'
                                        : `${company.teams.length} ${pluralize(
                                              company.teams.length,
                                              'team',
                                              'teams',
                                          )}: ${company.teams
                                              .slice(0, 3)
                                              .map(formatTeamLabel)
                                              .join(', ')}${company.teams.length > 3 ? '…' : ''}`;
                                return (
                                    <button
                                        key={company.companyId}
                                        id={optionId(index)}
                                        type="button"
                                        role="option"
                                        tabIndex={-1}
                                        aria-selected={selected}
                                        className={cn(
                                            'flex w-full items-start justify-between gap-2 rounded px-2 py-2 text-left text-sm',
                                            index === activeIndex && 'bg-neutral-100',
                                        )}
                                        onClick={() => choose(company)}
                                        onPointerMove={() => setActiveIndex(index)}
                                    >
                                        <span className="min-w-0">
                                            <span className="block font-medium text-neutral-950">
                                                {formatCompanyLabel(company)}
                                            </span>
                                            <span className="block text-xs text-neutral-500">
                                                {teamSummary}
                                            </span>
                                        </span>
                                        {selected ? (
                                            <Check
                                                aria-hidden="true"
                                                className="mt-0.5 size-4 shrink-0 text-neutral-700"
                                            />
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>
                        {optionsQuery.isFetching ? (
                            <p className="px-2 py-2 text-xs text-neutral-500" role="status">
                                Loading…
                            </p>
                        ) : null}
                        {!optionsQuery.isFetching && companies.length === 0 ? (
                            <p className="px-2 py-2 text-xs text-neutral-500" role="status">
                                No companies found.
                            </p>
                        ) : null}
                    </FieldControlContext.Provider>
                </div>
            ) : null}
        </div>
    );
}
